/**
 * Test Suite Page - 6-shot HDR/LNR comparison test
 * Displays results in a responsive grid with detailed metadata per shot
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Button, Space, Card,
  Spin, Skeleton, App, Image, Empty, Progress, Grid,
} from 'antd';
import AppLayout from '@/components/AppLayout';
import { MaterialIcon } from '@/components/MaterialIcon';
import { useEffectiveUserId } from '@/hooks/useEffectiveUserId';
import {
  subscribeToView, requestTestSuite,
  updateCameraSettings, updateTimerSettings,
} from '@/services/viewService';
import { SettingsDrawer } from '@/components/SettingsDrawer';
import { TestShotViewer } from '@/components/TestShotViewer';
import type { View, TestSuiteCommand, TestSuiteResult, CameraSettings, TimerSettings } from '@/types';
import JSZip from 'jszip';

const { Title, Text } = Typography;

// Mode display labels
const MODE_LABELS: Record<string, { label: string }> = {
  baseline: { label: 'Baseline' },
  hdr_low: { label: 'HDR Low' },
  hdr_medium: { label: 'HDR Medium' },
  hdr_strong: { label: 'HDR Strong' },
  lnr_classic: { label: 'LNR Classic' },
  lnr_fusion: { label: 'LNR Fusion' },
};

// All 6 modes in order (for skeleton cards before results arrive)
const ALL_MODES = ['baseline', 'hdr_low', 'hdr_medium', 'hdr_strong', 'lnr_classic', 'lnr_fusion'];

/** Fetch a URL as Blob with timeout */
async function fetchBlob(url: string, timeoutMs = 30000): Promise<Blob> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.blob();
  } finally {
    clearTimeout(timer);
  }
}

/** Trigger browser download from a Blob */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Result Card ────────────────────────────────────────────

function TestSuiteCell({
  result,
  mode,
  onDownload,
}: {
  result?: TestSuiteResult;
  mode: string;
  onDownload: (url: string, mode: string) => void;
}) {
  const modeInfo = MODE_LABELS[mode] || { label: mode };
  const isCompleted = result?.status === 'completed' && result.url;
  const isCapturing = result?.status === 'capturing';
  const isUploading = result?.status === 'uploading';
  const isError = result?.status === 'error';

  // Track whether the image has finished decoding so the dark overlay
  // gradient appears only when there is a visible photo behind it.
  // Preload via a detached Image() so we get a guaranteed onLoad even
  // when the browser served the file from cache (where the Antd <Image>
  // onLoad sometimes does not fire on rerender).
  const [imageLoaded, setImageLoaded] = useState(false);
  useEffect(() => {
    if (!result?.url) {
      setImageLoaded(false);
      return;
    }
    setImageLoaded(false);
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => { if (!cancelled) setImageLoaded(true); };
    img.onerror = () => { if (!cancelled) setImageLoaded(false); };
    img.src = result.url;
    if (img.complete && img.naturalWidth > 0) setImageLoaded(true);
    return () => { cancelled = true; };
  }, [result?.url]);
  const showOverlayDark = isCompleted && imageLoaded;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Image / placeholder */}
      {isCompleted && result.url ? (
        <Image
          src={result.url}
          alt={modeInfo.label}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (isCapturing || isUploading) ? (
        <div style={{ textAlign: 'center' }}>
          <Spin />
          <div style={{ marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {isCapturing ? 'Capturing...' : 'Uploading...'}
            </Text>
          </div>
        </div>
      ) : isError ? (
        <div style={{ textAlign: 'center', padding: 8 }}>
          <MaterialIcon name="error" style={{ fontSize: 24, color: '#ff4d4f' }} />
          <div style={{ marginTop: 4 }}>
            <Text type="danger" style={{ fontSize: 12 }}>{result?.error || 'Capture failed'}</Text>
          </div>
        </div>
      ) : (
        <MaterialIcon name="hourglass_empty" style={{ fontSize: 24, color: '#bfbfbf' }} />
      )}

      {/* Overlay: mode label + download icon, on top of the image.
          Gradient + white text only when an image is rendered behind it;
          otherwise plain dark text on the placeholder background. */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '4px 8px',
          background: showOverlayDark
            ? 'linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0))'
            : 'transparent',
          color: showOverlayDark ? '#fff' : 'rgba(0,0,0,0.85)',
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            fontWeight: 500,
            fontSize: 13,
            textShadow: showOverlayDark ? '0 1px 2px rgba(0,0,0,0.5)' : 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            flex: 1,
          }}
        >
          {modeInfo.label}
        </span>
        <Button
          type="text"
          size="small"
          icon={
            <MaterialIcon
              name="download"
              size={18}
              style={{ color: showOverlayDark ? '#fff' : undefined }}
            />
          }
          disabled={!isCompleted}
          onClick={() => isCompleted && onDownload(result.url!, mode)}
          style={{ pointerEvents: 'auto' }}
        />
      </div>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────

export default function TestSuitePage() {
  const { viewId } = useParams<{ viewId: string }>();
  const navigate = useNavigate();
  const { effectiveUid } = useEffectiveUserId();
  const { message } = App.useApp();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm; // <576px: stack the grid into a single column

  const [view, setView] = useState<View | null>(null);
  const [testSuite, setTestSuite] = useState<TestSuiteCommand | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [settingsDrawerVisible, setSettingsDrawerVisible] = useState(false);
  const [testShotVisible, setTestShotVisible] = useState(false);

  // Subscribe to view document (includes commands.testSuite)
  useEffect(() => {
    if (!effectiveUid || !viewId) return;

    const unsubscribe = subscribeToView(
      viewId,
      (v) => {
        setView(v);
        setTestSuite(v?.commands?.testSuite || null);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching view:', error);
        setLoading(false);
      },
      effectiveUid
    );

    return () => unsubscribe();
  }, [effectiveUid, viewId]);

  const isRunning = testSuite?.status === 'pending' || testSuite?.status === 'running';
  const completedCount = testSuite?.results?.filter(r => r.status === 'completed').length || 0;
  const hasResults = completedCount > 0;

  // Run test suite
  const handleRunTest = useCallback(async () => {
    if (!view || !effectiveUid) return;
    try {
      await requestTestSuite(effectiveUid, view.projectId, view.viewId);
      message.success('Test suite started');
    } catch (err) {
      message.error('Failed to start test suite');
      console.error(err);
    }
  }, [view, effectiveUid, message]);

  // Download single photo
  const handleDownloadSingle = useCallback(async (url: string, mode: string) => {
    try {
      const blob = await fetchBlob(url);
      downloadBlob(blob, `test_suite_${mode}.jpg`);
    } catch (err) {
      message.error('Download failed');
      console.error(err);
    }
  }, [message]);

  // Download all as ZIP
  const handleDownloadAll = useCallback(async () => {
    if (!testSuite?.results) return;
    const completedResults = testSuite.results.filter(r => r.status === 'completed' && r.url);
    if (completedResults.length === 0) return;

    setDownloading(true);
    try {
      const zip = new JSZip();

      for (const result of completedResults) {
        const blob = await fetchBlob(result.url!);
        zip.file(`${result.mode}.jpg`, blob);
      }

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'STORE', // JPEG already compressed
      });

      downloadBlob(zipBlob, `test_suite_${new Date().toISOString().slice(0, 10)}.zip`);
      message.success(`Downloaded ${completedResults.length} photos as ZIP`);
    } catch (err) {
      message.error('ZIP download failed');
      console.error(err);
    } finally {
      setDownloading(false);
    }
  }, [testSuite, message]);

  // Save camera settings (called by SettingsDrawer)
  const handleSaveCameraSettings = useCallback(async (cameraSettings: CameraSettings) => {
    if (!effectiveUid || !view) return;
    try {
      await updateCameraSettings(effectiveUid, view.projectId, view.viewId, cameraSettings);
    } catch (error) {
      console.error('Failed to save camera settings:', error);
      throw error;
    }
  }, [effectiveUid, view]);

  // Save timer settings (called by SettingsDrawer)
  const handleSaveTimerSettings = useCallback(async (
    capturing: boolean,
    timerSettings?: TimerSettings,
  ) => {
    if (!effectiveUid || !view) return;
    try {
      await updateTimerSettings(effectiveUid, view.projectId, view.viewId, capturing, timerSettings);
    } catch (error) {
      console.error('Failed to save timer settings:', error);
      throw error;
    }
  }, [effectiveUid, view]);

  if (loading) {
    // Single stable skeleton shaped like the real page (header + toolbar +
    // results grid) — everything reveals in one shot when the view arrives
    return (
      <AppLayout>
        <div style={{ padding: '16px 24px', maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Button
              type="text"
              icon={<MaterialIcon name="arrow_back" />}
              onClick={() => navigate('/')}
            />
            <Skeleton
              active
              title={false}
              paragraph={{ rows: 1, width: 160 }}
              style={{ margin: 0, maxWidth: 200 }}
            />
          </div>
          <div style={{ padding: '8px 0', marginBottom: 16 }}>
            <Skeleton active title={false} paragraph={{ rows: 1, width: 220 }} style={{ margin: 0 }} />
          </div>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gridTemplateRows: isMobile ? 'repeat(6, 1fr)' : 'repeat(2, 1fr)',
                aspectRatio: isMobile ? '4 / 18' : '2 / 1',
                columnGap: 2,
                rowGap: 2,
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              {ALL_MODES.map((mode) => (
                <div key={mode} style={{ background: '#f5f5f5' }} />
              ))}
            </div>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (!view) {
    return (
      <AppLayout>
        <Empty description="View not found" />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div style={{ padding: '16px 24px', maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Button
            type="text"
            icon={<MaterialIcon name="arrow_back" />}
            onClick={() => navigate('/')}
          />
          <Title level={4} style={{ margin: 0, flex: 1 }}>
            {view.name}
          </Title>
        </div>

        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16,
            padding: '8px 0',
            background: 'var(--ant-color-bg-layout)',
          }}
        >
          <Text type="secondary" style={{ whiteSpace: 'nowrap', flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Explore Variations
          </Text>
          <div style={{ flex: 1, minWidth: 0 }} />
          <Space style={{ flexShrink: 0, flexWrap: 'nowrap' }}>
            <Button
              type="primary"
              icon={<MaterialIcon name="play_arrow" style={{ fontSize: 16 }} />}
              onClick={handleRunTest}
              loading={isRunning}
              disabled={isRunning}
            >
              {isMobile ? '' : (isRunning ? 'Running...' : 'Run Test')}
            </Button>
            {hasResults && (
              <Button
                icon={<MaterialIcon name="folder_zip" style={{ fontSize: 16 }} />}
                onClick={handleDownloadAll}
                loading={downloading}
                disabled={isRunning}
              >
                {isMobile ? `(${completedCount})` : `Download All (${completedCount})`}
              </Button>
            )}
            <Button
              icon={<MaterialIcon name="settings_photo_camera" size={24} />}
              onClick={() => setSettingsDrawerVisible(true)}
            />
          </Space>
        </div>

        {/* Progress bar when running */}
        {isRunning && (
          <div style={{ marginBottom: 16 }}>
            <Progress
              percent={Math.round((completedCount / 6) * 100)}
              format={() => `${completedCount}/6`}
              status="active"
            />
          </div>
        )}

        {/* Error banner */}
        {testSuite?.status === 'error' && testSuite.error && (
          <Card size="small" style={{ marginBottom: 16, borderColor: '#ff4d4f' }}>
            <Space>
              <MaterialIcon name="error" style={{ color: '#ff4d4f' }} />
              <Text type="danger">{testSuite.error}</Text>
            </Space>
          </Card>
        )}

        {/* Results — 3×2 grid, cells separated by a 2px white gutter on a tinted background */}
        {testSuite && (
          <Card size="small" styles={{ body: { padding: 12 } }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gridTemplateRows: isMobile ? 'repeat(6, 1fr)' : 'repeat(2, 1fr)',
              aspectRatio: isMobile ? '4 / 18' : '2 / 1',
              columnGap: 2,
              rowGap: 2,
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            {ALL_MODES.map((mode, index) => {
              const result = testSuite?.results?.find(r => r.mode === mode)
                || (testSuite?.results?.[index]?.mode === '' ? undefined : testSuite?.results?.[index]);
              return (
                <TestSuiteCell
                  key={mode}
                  result={result}
                  mode={mode}
                  onDownload={handleDownloadSingle}
                />
              );
            })}
          </div>
          </Card>
        )}

{/* Empty state when no test has been run yet */}
        {!testSuite && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ marginTop: 32, marginBottom: 16 }}
            description={
              <Text type="secondary">
                No test results yet. Press "Run Test" to capture 6 photos with different exposure and noise reduction modes for comparison.
              </Text>
            }
          >
            <Button
              type="primary"
              icon={<MaterialIcon name="play_arrow" style={{ fontSize: 16 }} />}
              onClick={handleRunTest}
              loading={isRunning}
              disabled={isRunning}
            >
              Run Test
            </Button>
          </Empty>
        )}
      </div>

      <SettingsDrawer
        open={settingsDrawerVisible}
        view={view ?? null}
        onClose={() => setSettingsDrawerVisible(false)}
        onTestShot={() => setTestShotVisible(true)}
        onSaveCameraSettings={handleSaveCameraSettings}
        onSaveTimerSettings={handleSaveTimerSettings}
      />

      {testShotVisible && view && (
        <TestShotViewer
          view={view}
          onClose={() => setTestShotVisible(false)}
        />
      )}
    </AppLayout>
  );
}
